import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  History as HistoryIcon,
  BarChart as BarChartIcon,
  Email as EmailIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

type TriggerType =
  | 'appointment_scheduled'
  | 'appointment_confirmed'
  | 'appointment_cancelled'
  | 'appointment_rescheduled'
  | 'appointment_reminder'
  | 'crm_stage'
  | 'product_purchase'
  | 'abandoned_cart';

const TRIGGER_LABELS: Record<TriggerType, string> = {
  appointment_scheduled: 'Consulta Agendada',
  appointment_confirmed: 'Consulta Confirmada',
  appointment_cancelled: 'Consulta Cancelada',
  appointment_rescheduled: 'Consulta Reagendada',
  appointment_reminder: 'Lembrete de Consulta',
  crm_stage: 'Estágio do CRM',
  product_purchase: 'Compra de Produto',
  abandoned_cart: 'Carrinho Abandonado',
};

const CLINIC_TRIGGER_TYPES: TriggerType[] = [
  'appointment_scheduled',
  'appointment_confirmed',
  'appointment_cancelled',
  'appointment_rescheduled',
  'appointment_reminder',
];

const APPOINTMENT_TYPE_OPTIONS = [
  { value: 'consultation', label: 'Consulta' },
  { value: 'return', label: 'Retorno' },
  { value: 'procedure', label: 'Procedimento' },
  { value: 'exam', label: 'Exame' },
  { value: 'surgery', label: 'Cirurgia' },
  { value: 'emergency', label: 'Emergência' },
  { value: 'telemedicine', label: 'Telemedicina' },
  { value: 'evaluation', label: 'Avaliação' },
  { value: 'follow_up', label: 'Acompanhamento' },
];

const CLINIC_TEMPLATE_VARIABLES = [
  { var: '{{patient_name}}', desc: 'Nome do paciente' },
  { var: '{{patient_email}}', desc: 'Email do paciente' },
  { var: '{{patient_phone}}', desc: 'Telefone do paciente' },
  { var: '{{appointment_date}}', desc: 'Data da consulta' },
  { var: '{{appointment_time}}', desc: 'Horário da consulta' },
  { var: '{{appointment_type}}', desc: 'Tipo da consulta' },
  { var: '{{professional_name}}', desc: 'Nome do profissional' },
  { var: '{{professional_specialty}}', desc: 'Especialidade' },
  { var: '{{clinic_name}}', desc: 'Nome da clínica' },
  { var: '{{clinic_phone}}', desc: 'Telefone da clínica' },
  { var: '{{clinic_address}}', desc: 'Endereço da clínica' },
];

interface EmailAutomation {
  id: string;
  name: string;
  description?: string;
  trigger_type: TriggerType;
  trigger_config: {
    stage_ids?: string[];
    products?: string[];
    appointment_types?: string[];
    reminder_hours_before?: number;
  };
  template_id: string;
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  status: 'active' | 'paused' | 'inactive';
  is_active: boolean;
  created_at: string;
}

interface AutomationStats {
  total: number;
  pending: number;
  executed: number;
  failed: number;
  cancelled: number;
  opened: number;
  clicked: number;
  bounced: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
}

interface ExecutionHistory {
  id: string;
  contact_id: string;
  patient_id?: string;
  recipient_email: string;
  email_subject: string;
  status: string;
  scheduled_at: string;
  executed_at: string;
  email_opened_at?: string;
  email_clicked_at?: string;
  error_message?: string;
  contacts?: {
    id: string;
    name: string;
    email: string;
    phone: string;
  } | null;
  patient?: {
    id: string;
    full_name: string;
    email: string;
    phone: string;
  } | null;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
}

interface CrmStage {
  id: string;
  name: string;
  pipeline_id: string;
}

const steps = ['Tipo de Trigger', 'Configuração', 'Template e Delay'];

export default function EmailAutomations() {
  const [automations, setAutomations] = useState<EmailAutomation[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [crmStages, setCrmStages] = useState<CrmStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openStatsDialog, setOpenStatsDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingAutomation, setEditingAutomation] = useState<EmailAutomation | null>(null);
  const [selectedAutomationId, setSelectedAutomationId] = useState<string | null>(null);
  const [history, setHistory] = useState<ExecutionHistory[]>([]);
  const [stats, setStats] = useState<AutomationStats | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [testAutomation, setTestAutomation] = useState<EmailAutomation | null>(null);
  const [loadingTest, setLoadingTest] = useState(false);
  const [testData, setTestData] = useState({
    name: '',
    email: '',
    phone: '',
    platform: '',
    product: '',
    template_id: '',
    automation_id: '',
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'appointment_scheduled' as TriggerType,
    stage_ids: [] as string[],
    products: [] as string[],
    appointment_types: [] as string[],
    reminder_hours_before: 24,
    template_id: '',
    delay_days: 0,
    delay_hours: 0,
    delay_minutes: 0,
    status: 'active' as 'active' | 'paused' | 'inactive',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [automationsRes, templatesRes] = await Promise.all([
        axios.get(`${getApiUrl()}/email-automations`),
        axios.get(`${getApiUrl()}/email/templates`),
      ]);

      setAutomations(automationsRes.data);
      setTemplates(templatesRes.data);

      // Try to load CRM stages (optional, may not be configured)
      try {
        const stagesRes = await axios.get(`${getApiUrl()}/crm/pipelines`);
        const allStages: CrmStage[] = [];
        stagesRes.data.data?.forEach((pipeline: any) => {
          pipeline.stages?.forEach((stage: any) => {
            allStages.push({
              id: stage.id,
              name: stage.name,
              pipeline_id: pipeline.id,
            });
          });
        });
        setCrmStages(allStages);
      } catch {
        // CRM not configured — not critical
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (automation?: EmailAutomation) => {
    if (automation) {
      setEditingAutomation(automation);
      setFormData({
        name: automation.name,
        description: automation.description || '',
        trigger_type: automation.trigger_type,
        stage_ids: automation.trigger_config.stage_ids || [],
        products: automation.trigger_config.products || [],
        appointment_types: automation.trigger_config.appointment_types || [],
        reminder_hours_before: automation.trigger_config.reminder_hours_before || 24,
        template_id: automation.template_id,
        delay_days: automation.delay_days || 0,
        delay_hours: automation.delay_hours,
        delay_minutes: automation.delay_minutes,
        status: automation.status,
      });
    } else {
      setEditingAutomation(null);
      setFormData({
        name: '',
        description: '',
        trigger_type: 'appointment_scheduled',
        stage_ids: [],
        products: [],
        appointment_types: [],
        reminder_hours_before: 24,
        template_id: '',
        delay_days: 0,
        delay_hours: 0,
        delay_minutes: 0,
        status: 'active',
      });
    }
    setActiveStep(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAutomation(null);
    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.trigger_type) {
        alert('Selecione um tipo de trigger');
        return;
      }
    } else if (activeStep === 1) {
      if (formData.trigger_type === 'crm_stage' && formData.stage_ids.length === 0) {
        alert('Selecione pelo menos um estágio do CRM');
        return;
      }
      if (formData.trigger_type === 'product_purchase' && formData.products.length === 0) {
        alert('Informe pelo menos um produto');
        return;
      }
      // Clinic triggers don't have mandatory step 2 config — appointment_types is optional
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.template_id) {
        alert('Preencha todos os campos obrigatórios');
        return;
      }

      const isClinicTrigger = CLINIC_TRIGGER_TYPES.includes(formData.trigger_type);
      const triggerConfig: any = {};

      if (formData.trigger_type === 'crm_stage') {
        triggerConfig.stage_ids = formData.stage_ids;
      } else if (formData.trigger_type === 'product_purchase') {
        triggerConfig.products = formData.products;
      } else if (isClinicTrigger) {
        if (formData.appointment_types.length > 0) {
          triggerConfig.appointment_types = formData.appointment_types;
        }
        if (formData.trigger_type === 'appointment_reminder') {
          triggerConfig.reminder_hours_before = formData.reminder_hours_before || 24;
        }
      }

      const payload = {
        name: formData.name,
        description: formData.description,
        trigger_type: formData.trigger_type,
        trigger_config: triggerConfig,
        template_id: formData.template_id,
        delay_days: formData.delay_days,
        delay_hours: formData.delay_hours,
        delay_minutes: formData.delay_minutes,
        status: formData.status,
      };

      if (editingAutomation) {
        await axios.put(`${getApiUrl()}/email-automations/${editingAutomation.id}`, payload);
      } else {
        await axios.post(`${getApiUrl()}/email-automations`, payload);
      }

      await loadData();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Erro ao salvar automação:', error);
      alert(error.response?.data?.message || 'Erro ao salvar automação');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar esta automação?')) {
      return;
    }

    try {
      await axios.delete(`${getApiUrl()}/email-automations/${id}`);
      await loadData();
    } catch (error: any) {
      console.error('Erro ao deletar automação:', error);
      alert(error.response?.data?.message || 'Erro ao deletar automação');
    }
  };

  const handleToggleStatus = async (automation: EmailAutomation) => {
    try {
      const newStatus = automation.status === 'active' ? 'paused' : 'active';
      await axios.put(`${getApiUrl()}/email-automations/${automation.id}`, {
        status: newStatus,
      });
      await loadData();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      alert(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleViewHistory = async (automationId: string) => {
    try {
      setSelectedAutomationId(automationId);
      setLoadingHistory(true);
      const response = await axios.get(`${getApiUrl()}/email-automations/${automationId}/history`);
      setHistory(response.data);
      setOpenHistoryDialog(true);
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      alert(error.response?.data?.message || 'Erro ao carregar histórico');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewStats = async (automationId: string) => {
    try {
      setSelectedAutomationId(automationId);
      setLoadingStats(true);
      const response = await axios.get(`${getApiUrl()}/email-automations/${automationId}/stats`);
      setStats(response.data);
      setOpenStatsDialog(true);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
      alert(error.response?.data?.message || 'Erro ao carregar estatísticas');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleOpenTestDialog = (automation: EmailAutomation) => {
    setTestAutomation(automation); // Salvar automação para mostrar delay
    setTestData({
      name: '',
      email: '',
      phone: '',
      platform: '',
      product: '',
      template_id: automation.template_id,
      automation_id: automation.id, // Incluir automation_id para respeitar delay
    });
    setOpenTestDialog(true);
  };

  const handleSendTest = async () => {
    if (!testData.email || !testData.template_id) {
      alert('Email e template são obrigatórios');
      return;
    }

    // Validar formato do email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testData.email)) {
      alert('Email inválido');
      return;
    }

    const emailToSend = testData.email;
    const nameToSend = testData.name;
    const phoneToSend = testData.phone;
    const templateIdToSend = testData.template_id;
    const automationIdToSend = testData.automation_id;
    const currentAutomation = testAutomation;
    const hasDelay = currentAutomation && (currentAutomation.delay_days > 0 || currentAutomation.delay_hours > 0 || currentAutomation.delay_minutes > 0);

    setOpenTestDialog(false);
    setTestAutomation(null);
    setTestData({
      name: '',
      email: '',
      phone: '',
      platform: '',
      product: '',
      template_id: '',
      automation_id: '',
    });

    try {
      setLoadingTest(true);
      const response = await axios.post(`${getApiUrl()}/email-automations/test`, {
        name: nameToSend,
        email: emailToSend,
        phone: phoneToSend,
        template_id: templateIdToSend,
        automation_id: automationIdToSend,
      });
      
      // Mostrar mensagem informativa
      if (hasDelay && currentAutomation) {
        const delayParts: string[] = [];
        if (currentAutomation.delay_days > 0) {
          delayParts.push(`${currentAutomation.delay_days} dia(s)`);
        }
        if (currentAutomation.delay_hours > 0) {
          delayParts.push(`${currentAutomation.delay_hours} hora(s)`);
        }
        if (currentAutomation.delay_minutes > 0) {
          delayParts.push(`${currentAutomation.delay_minutes} minuto(s)`);
        }
        const delayText = delayParts.join(', ');
        alert(`Email de teste agendado para ${emailToSend}!\n\nO email será enviado após o delay configurado (${delayText}).\n\nO processamento está em andamento em background.`);
      } else {
        alert(`Email de teste agendado para ${emailToSend}!\n\nO processamento está em andamento em background.`);
      }
    } catch (error: any) {
      console.error('Erro ao enviar email de teste:', error);
      alert(error.response?.data?.message || 'Erro ao agendar email de teste');
    } finally {
      setLoadingTest(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#3b82f6' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{
        background: '#ffffff',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        p: 4,
        mb: 6,
        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)',
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <EmailIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
            <Box>
              <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                Automações de Email
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Configure automações de email para eventos clínicos e do CRM
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
              color: '#ffffff',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
              borderRadius: 3,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Nova Automação
          </Button>
        </Box>
      </Box>

      {/* Automation Cards */}
      <Grid container spacing={3}>
        {automations.map((automation) => (
          <Grid item xs={12} md={6} lg={4} key={automation.id}>
            <Box sx={{
              background: '#ffffff',
              border: '2px solid #3b82f6',
              borderRadius: '12px',
              p: 4,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600, mb: 0.5 }}>
                    {automation.name}
                  </Typography>
                  {automation.description && (
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {automation.description}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={automation.status === 'active' ? 'Ativa' : 'Pausada'}
                  sx={{
                    backgroundColor: automation.status === 'active' ? '#d1fae5' : '#e5e7eb',
                    color: automation.status === 'active' ? '#065f46' : '#374151',
                    border: automation.status === 'active' ? '1px solid #10b981' : '1px solid #9ca3af',
                    fontWeight: 600,
                  }}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                  <strong style={{ color: '#000000' }}>Trigger:</strong>{' '}
                  {TRIGGER_LABELS[automation.trigger_type as TriggerType] || automation.trigger_type}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  <strong style={{ color: '#000000' }}>Delay:</strong>{' '}
                  {automation.delay_days > 0 && `${automation.delay_days}d `}
                  {automation.delay_hours > 0 && `${automation.delay_hours}h `}
                  {automation.delay_minutes > 0 && `${automation.delay_minutes}min`}
                  {automation.delay_days === 0 && automation.delay_hours === 0 && automation.delay_minutes === 0 && 'Imediato'}
                </Typography>
              </Box>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Tooltip title="Testar Automação">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenTestDialog(automation)}
                    sx={{
                      color: '#10b981',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ver Estatísticas">
                  <IconButton
                    size="small"
                    onClick={() => handleViewStats(automation.id)}
                    sx={{
                      color: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <BarChartIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ver Histórico">
                  <IconButton
                    size="small"
                    onClick={() => handleViewHistory(automation.id)}
                    sx={{
                      color: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={automation.status === 'active' ? 'Pausar' : 'Ativar'}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleStatus(automation)}
                    sx={{
                      color: automation.status === 'active' ? '#f59e0b' : '#10b981',
                      backgroundColor: automation.status === 'active' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                      border: automation.status === 'active' ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)',
                      '&:hover': {
                        backgroundColor: automation.status === 'active' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    {automation.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(automation)}
                    sx={{
                      color: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Deletar">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(automation.id)}
                    sx={{
                      color: '#ef4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.2)',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        ))}

        {automations.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{
              background: '#ffffff',
              border: '2px solid #3b82f6',
              borderRadius: '12px',
              p: 6,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
            }}>
              <EmailIcon sx={{ color: '#3b82f6', fontSize: 64, mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#000000', mb: 1, fontWeight: 600 }}>
                Nenhuma automação criada
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                Clique em "Nova Automação" para começar
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Dialog de criação/edição */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>
          {editingAutomation ? 'Editar Automação' : 'Nova Automação de Email'}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ '& .MuiStepLabel-label': { color: '#000000' } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Tipo de Trigger */}
          {activeStep === 0 && (
            <Box>
              <TextField
                fullWidth
                label="Nome da Automação"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '1px solid #3b82f6',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused': {
                      borderColor: '#3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: '#3b82f6',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000000',
                  },
                }}
              />

              <TextField
                fullWidth
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '1px solid #3b82f6',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused': {
                      borderColor: '#3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: '#3b82f6',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#000000',
                  },
                }}
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#3b82f6' } }}>Evento que dispara o email</InputLabel>
                <Select
                  value={formData.trigger_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      trigger_type: e.target.value as TriggerType,
                      stage_ids: [],
                      products: [],
                      appointment_types: [],
                    })
                  }
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '1px solid #3b82f6',
                    '& fieldset': { border: 'none' },
                    '&:hover': { borderColor: '#2563eb' },
                    '&.Mui-focused': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' },
                  }}
                >
                  <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Eventos Clínicos
                  </MenuItem>
                  <MenuItem value="appointment_scheduled">Consulta Agendada</MenuItem>
                  <MenuItem value="appointment_confirmed">Consulta Confirmada</MenuItem>
                  <MenuItem value="appointment_cancelled">Consulta Cancelada</MenuItem>
                  <MenuItem value="appointment_rescheduled">Consulta Reagendada</MenuItem>
                  <MenuItem value="appointment_reminder">Lembrete de Consulta</MenuItem>
                  <MenuItem disabled sx={{ opacity: 0.6, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, mt: 1 }}>
                    CRM / Infoprodutos
                  </MenuItem>
                  <MenuItem value="crm_stage">Estágio do CRM</MenuItem>
                  <MenuItem value="product_purchase">Compra de Produto</MenuItem>
                  <MenuItem value="abandoned_cart">Carrinho Abandonado</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Step 2: Configuração do Trigger */}
          {activeStep === 1 && (
            <Box>
              {CLINIC_TRIGGER_TYPES.includes(formData.trigger_type) && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 600, mb: 1 }}>
                    Filtrar por tipo de consulta (opcional)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    Deixe em branco para disparar para todos os tipos de consulta
                  </Typography>
                  <Autocomplete
                    multiple
                    options={APPOINTMENT_TYPE_OPTIONS}
                    getOptionLabel={(option) => option.label}
                    value={APPOINTMENT_TYPE_OPTIONS.filter((o) => formData.appointment_types.includes(o.value))}
                    onChange={(_, newValue) => {
                      setFormData({
                        ...formData,
                        appointment_types: newValue.map((v) => v.value),
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipos de Consulta"
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                            color: '#000000',
                            border: '1px solid #3b82f6',
                            '& fieldset': { border: 'none' },
                          },
                          '& .MuiInputLabel-root': { color: '#64748b' },
                        }}
                      />
                    )}
                  />

                  {formData.trigger_type === 'appointment_reminder' && (
                    <TextField
                      fullWidth
                      label="Horas antes da consulta para enviar o lembrete"
                      type="number"
                      value={formData.reminder_hours_before}
                      onChange={(e) =>
                        setFormData({ ...formData, reminder_hours_before: parseInt(e.target.value) || 24 })
                      }
                      margin="normal"
                      inputProps={{ min: 1, max: 168 }}
                      helperText="Ex: 24 = lembrete 24 horas antes. O sistema verifica a cada minuto."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#000000',
                          border: '1px solid #3b82f6',
                          '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputLabel-root': { color: '#64748b' },
                        '& .MuiFormHelperText-root': { color: '#94a3b8' },
                      }}
                    />
                  )}

                  <Alert
                    severity="info"
                    sx={{
                      mt: 2,
                      backgroundColor: '#eff6ff',
                      color: '#1e40af',
                      border: '1px solid #93c5fd',
                      '& .MuiAlert-icon': { color: '#3b82f6' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Variáveis disponíveis no template:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {CLINIC_TEMPLATE_VARIABLES.map((v) => (
                        <Chip
                          key={v.var}
                          label={`${v.var} — ${v.desc}`}
                          size="small"
                          sx={{
                            backgroundColor: '#dbeafe',
                            color: '#1e40af',
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                      ))}
                    </Box>
                  </Alert>
                </Box>
              )}

              {formData.trigger_type === 'crm_stage' && (
                <Autocomplete
                  multiple
                  options={crmStages}
                  getOptionLabel={(option) => option.name}
                  value={crmStages.filter((s) => formData.stage_ids.includes(s.id))}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      stage_ids: newValue.map((v) => v.id),
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecionar Estágios"
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#000000',
                          border: '1px solid #3b82f6',
                          '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputLabel-root': { color: '#64748b' },
                      }}
                    />
                  )}
                />
              )}

              {formData.trigger_type === 'product_purchase' && (
                <Box>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    Digite os nomes dos produtos (um por linha ou separados por vírgula)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Produtos"
                    value={formData.products.join(', ')}
                    onChange={(e) => {
                      const products = e.target.value
                        .split(/[,\n]/)
                        .map((p: string) => p.trim())
                        .filter((p: string) => p.length > 0);
                      setFormData({ ...formData, products });
                    }}
                    margin="normal"
                    required
                    multiline
                    rows={4}
                    helperText="Separe os produtos por vírgula ou quebra de linha."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        border: '1px solid #3b82f6',
                        '& fieldset': { border: 'none' },
                      },
                      '& .MuiInputLabel-root': { color: '#64748b' },
                      '& .MuiFormHelperText-root': { color: '#94a3b8' },
                    }}
                  />
                </Box>
              )}

              {formData.trigger_type === 'abandoned_cart' && (
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor: '#eff6ff',
                    color: '#1e40af',
                    border: '1px solid #93c5fd',
                    '& .MuiAlert-icon': { color: '#3b82f6' },
                  }}
                >
                  Esta automação será disparada automaticamente quando um carrinho for abandonado.
                </Alert>
              )}
            </Box>
          )}

          {/* Step 3: Template e Delay */}
          {activeStep === 2 && (
            <Box>
              <FormControl fullWidth margin="normal" required>
                <InputLabel sx={{ color: '#64748b', '&.Mui-focused': { color: '#3b82f6' } }}>Selecionar Template</InputLabel>
                <Select
                  value={formData.template_id}
                  onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: '1px solid #3b82f6',
                    '& fieldset': { border: 'none' },
                    '&:hover': { borderColor: '#2563eb' },
                    '&.Mui-focused': { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' },
                  }}
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#000000', fontWeight: 600 }}>
                Delay após o evento (tempo de espera antes de enviar)
              </Typography>
              <Box display="flex" gap={2} mt={1}>
                <TextField
                  label="Dias"
                  type="number"
                  value={formData.delay_days}
                  onChange={(e) =>
                    setFormData({ ...formData, delay_days: parseInt(e.target.value) || 0 })
                  }
                  inputProps={{ min: 0 }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      border: '1px solid #3b82f6',
                      '& fieldset': { border: 'none' },
                    },
                    '& .MuiInputLabel-root': { color: '#64748b' },
                  }}
                />
                <TextField
                  label="Horas"
                  type="number"
                  value={formData.delay_hours}
                  onChange={(e) =>
                    setFormData({ ...formData, delay_hours: parseInt(e.target.value) || 0 })
                  }
                  inputProps={{ min: 0, max: 23 }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      border: '1px solid #3b82f6',
                      '& fieldset': { border: 'none' },
                    },
                    '& .MuiInputLabel-root': { color: '#64748b' },
                  }}
                />
                <TextField
                  label="Minutos"
                  type="number"
                  value={formData.delay_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, delay_minutes: parseInt(e.target.value) || 0 })
                  }
                  inputProps={{ min: 0, max: 59 }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      border: '1px solid #3b82f6',
                      '& fieldset': { border: 'none' },
                    },
                    '& .MuiInputLabel-root': { color: '#64748b' },
                  }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 'active'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.checked ? 'active' : 'paused',
                      })
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3b82f6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3b82f6',
                      },
                    }}
                  />
                }
                label="Ativar automação"
                sx={{ mt: 2, color: '#000000' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Cancelar
          </Button>
          {activeStep > 0 && (
            <Button 
              onClick={handleBack}
              sx={{
                color: '#64748b',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#e2e8f0',
                  color: '#475569',
                }
              }}
            >
              Voltar
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button 
              onClick={handleNext} 
              variant="contained"
              sx={{
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#2563eb',
                },
              }}
            >
              Próximo
            </Button>
          ) : (
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#2563eb',
                },
              }}
            >
              {editingAutomation ? 'Atualizar' : 'Criar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de Estatísticas */}
      <Dialog 
        open={openStatsDialog} 
        onClose={() => setOpenStatsDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>
          Estatísticas da Automação
        </DialogTitle>
        <DialogContent>
          {loadingStats ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress sx={{ color: '#3b82f6' }} />
            </Box>
          ) : stats ? (
            <Box>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                {[
                  { label: 'Total', value: stats.total, color: '#3b82f6' },
                  { label: 'Enviados', value: stats.executed, color: '#10b981' },
                  { label: 'Pendentes', value: stats.pending, color: '#f59e0b' },
                  { label: 'Falhas', value: stats.failed, color: '#ef4444' },
                  { label: 'Abertos', value: stats.opened, color: '#3b82f6' },
                  { label: 'Cliques', value: stats.clicked, color: '#3b82f6' },
                  { label: 'Taxa de Abertura', value: `${stats.open_rate.toFixed(1)}%`, color: '#3b82f6' },
                  { label: 'Taxa de Clique', value: `${stats.click_rate.toFixed(1)}%`, color: '#3b82f6' },
                ].map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Box sx={{ 
                      background: '#ffffff',
                      border: '2px solid #3b82f6',
                      borderRadius: '12px',
                      textAlign: 'center', 
                      py: 2,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
                    }}>
                      <Typography variant="h4" sx={{ color: stat.color, fontWeight: 700 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Alert 
              severity="info"
              sx={{
                backgroundColor: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #3b82f6',
                '& .MuiAlert-icon': {
                  color: '#3b82f6',
                },
              }}
            >
              Nenhuma estatística disponível
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={() => setOpenStatsDialog(false)}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Histórico */}
      <Dialog 
        open={openHistoryDialog} 
        onClose={() => setOpenHistoryDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>
          Histórico de Execuções
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress sx={{ color: '#3b82f6' }} />
            </Box>
          ) : history.length > 0 ? (
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Contato</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Email</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Assunto</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Status</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Agendado</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Executado</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Aberto</TableCell>
                    <TableCell sx={{ color: '#000000', fontWeight: 'bold', backgroundColor: '#eff6ff' }}>Clique</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((execution) => (
                    <TableRow 
                      key={execution.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#eff6ff',
                        },
                        '& td': {
                          color: '#000000',
                          borderBottom: '1px solid #e5e7eb',
                        },
                      }}
                    >
                      <TableCell>{execution.patient?.full_name || execution.contacts?.name || execution.recipient_email || '-'}</TableCell>
                      <TableCell>{execution.recipient_email || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={execution.email_subject || '-'}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, color: '#000000' }}>
                            {execution.email_subject || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={execution.status}
                          sx={{
                            backgroundColor: 
                              execution.status === 'executed' ? '#d1fae5' :
                              execution.status === 'failed' ? '#fee2e2' :
                              execution.status === 'pending' ? '#fef3c7' :
                              '#e5e7eb',
                            color: 
                              execution.status === 'executed' ? '#065f46' :
                              execution.status === 'failed' ? '#991b1b' :
                              execution.status === 'pending' ? '#92400e' :
                              '#374151',
                            border: 
                              execution.status === 'executed' ? '1px solid #10b981' :
                              execution.status === 'failed' ? '1px solid #ef4444' :
                              execution.status === 'pending' ? '1px solid #f59e0b' :
                              '1px solid #9ca3af',
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {execution.scheduled_at
                          ? new Date(execution.scheduled_at).toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {execution.executed_at
                          ? new Date(execution.executed_at).toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {execution.email_opened_at
                          ? new Date(execution.email_opened_at).toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {execution.email_clicked_at
                          ? new Date(execution.email_clicked_at).toLocaleString('pt-BR')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2,
                backgroundColor: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #3b82f6',
                '& .MuiAlert-icon': {
                  color: '#3b82f6',
                },
              }}
            >
              Nenhuma execução encontrada
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={() => setOpenHistoryDialog(false)}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Teste */}
      <Dialog open={openTestDialog} onClose={() => setOpenTestDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
                    color: '#ffffff',
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2
        }}>Enviar Email de Teste</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            fullWidth
              label="Nome do Paciente"
              value={testData.name}
              onChange={(e) => setTestData({ ...testData, name: e.target.value })}
              placeholder="Ex: João Silva"
            />
            <TextField
              fullWidth
              label="Email do Paciente"
            type="email"
              value={testData.email}
              onChange={(e) => setTestData({ ...testData, email: e.target.value })}
              required
            placeholder="paciente@email.com"
            />
            <TextField
              fullWidth
              label="Telefone"
              value={testData.phone}
              onChange={(e) => setTestData({ ...testData, phone: e.target.value })}
              placeholder="Ex: (31) 99999-9999"
            />
            <FormControl fullWidth required>
              <InputLabel>Template</InputLabel>
              <Select
                value={testData.template_id}
                onChange={(e) => setTestData({ ...testData, template_id: e.target.value })}
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: 1, backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #93c5fd', '& .MuiAlert-icon': { color: '#3b82f6' } }}>
              <Typography variant="body2">
                As variáveis do template serão substituídas com dados de teste. As variáveis clínicas (paciente, consulta, clínica) serão preenchidas com valores de exemplo.
              </Typography>
            </Alert>
            {testAutomation && (testAutomation.delay_days > 0 || testAutomation.delay_hours > 0 || testAutomation.delay_minutes > 0) && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Delay configurado será respeitado:</strong> O email será enviado após{' '}
                  {testAutomation.delay_days > 0 && `${testAutomation.delay_days} dia(s) `}
                  {testAutomation.delay_hours > 0 && `${testAutomation.delay_hours} hora(s) `}
                  {testAutomation.delay_minutes > 0 && `${testAutomation.delay_minutes} minuto(s)`}
                  {testAutomation.delay_days === 0 && testAutomation.delay_hours === 0 && testAutomation.delay_minutes === 0 && 'Imediato'}
                  . Aguarde o processamento.
              </Typography>
              </Alert>
          )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #3b82f6', 
          backgroundColor: '#f8fafc',
          p: 2 
        }}>
          <Button 
            onClick={() => setOpenTestDialog(false)}
            sx={{
              color: '#64748b',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#e2e8f0',
                color: '#475569',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSendTest}
            variant="contained"
            disabled={loadingTest || !testData.email || !testData.template_id}
            startIcon={loadingTest ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#2563eb',
              },
              '&:disabled': {
                backgroundColor: '#cbd5e1',
                color: '#94a3b8',
              }
            }}
          >
            {loadingTest ? 'Enviando...' : 'Enviar Teste'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


